import { useState } from 'react';
import { useContactCalendar, useIntegrations, useCreateActivity } from '../hooks';
import { integrationsApi } from '../api';
import type { M365CalendarEvent } from '../types';

interface Props {
  contactId: string;
}

function formatEventDate(dt: string): string {
  return new Date(dt).toLocaleString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/* ── Detail-Popup ── */

function CalendarDetailModal({
  event,
  contactId,
  onClose,
}: {
  event: M365CalendarEvent;
  contactId: string;
  onClose: () => void;
}) {
  const createActivity = useCreateActivity();
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [comment, setComment] = useState('');

  const organizerName = event.organizer?.emailAddress?.name ?? event.organizer?.emailAddress?.address ?? '';
  const organizerAddr = event.organizer?.emailAddress?.address ?? '';
  const attendeeDetails = (event.attendees ?? []).map((a) => {
    const name = a.emailAddress?.name ?? '';
    const addr = a.emailAddress?.address ?? '';
    return name ? `${name} <${addr}>` : addr;
  });
  const attendeeNames = attendeeDetails.join(', ');

  // HTML-Tags entfernen fuer Plain-Text Darstellung
  function stripHtml(html: string): string {
    return html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<\/div>/gi, '\n')
      .replace(/<\/li>/gi, '\n')
      .replace(/<li[^>]*>/gi, '• ')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  async function handleSave() {
    if (saveState === 'saved') return;
    setSaveState('saving');
    try {
      // Vollstaendiges Abbild des Kalendereintrags
      const lines: string[] = [];
      lines.push(`Zeitraum: ${formatEventDate(event.start.dateTime)} - ${new Date(event.end.dateTime).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`);
      lines.push(`Organisator: ${organizerName}${organizerAddr ? ` <${organizerAddr}>` : ''}`);
      if (attendeeDetails.length > 0) lines.push(`Teilnehmer: ${attendeeNames}`);
      if (event.location?.displayName) lines.push(`Ort: ${event.location.displayName}`);
      if (event.isOnlineMeeting) lines.push('Online-Meeting: Ja');
      if (event.onlineMeetingUrl) lines.push(`Meeting-Link: ${event.onlineMeetingUrl}`);
      // Vollstaendigen Body/Beschreibung einfuegen wenn vorhanden
      if (event.body?.content) {
        lines.push('');
        lines.push('--- Beschreibung ---');
        const bodyText = event.body.contentType === 'html'
          ? stripHtml(event.body.content)
          : event.body.content;
        if (bodyText) lines.push(bodyText);
      }
      if (comment.trim()) {
        lines.push('');
        lines.push('--- Kommentar ---');
        lines.push(comment.trim());
      }

      await createActivity.mutateAsync({
        contactId,
        type: 'MEETING',
        subject: event.subject ?? '(kein Betreff)',
        description: lines.join('\n'),
        scheduledAt: event.start.dateTime,
        completedAt: event.end.dateTime,
      });
      setSaveState('saved');
    } catch {
      setSaveState('error');
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--color-bg-card)', borderRadius: 'var(--radius-md)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)', width: '100%', maxWidth: 520,
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '1.25rem 1.25rem 0.75rem',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: '1.0625rem', fontWeight: 600, margin: 0, color: 'var(--color-text)' }}>
              {event.subject}
            </h2>
            {event.isOnlineMeeting && (
              <span style={{
                display: 'inline-block', marginTop: '0.25rem',
                fontSize: '0.6875rem', fontWeight: 600,
                padding: '0.125rem 0.375rem', background: '#dbeafe', color: '#1e40af',
                borderRadius: '999px',
              }}>
                Online-Meeting
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '1.25rem', color: 'var(--color-text-muted)', padding: '0.25rem',
            }}
          >
            ×
          </button>
        </div>

        {/* Action buttons */}
        <div style={{
          display: 'flex', gap: '0.5rem', padding: '0 1.25rem', marginBottom: '1rem',
        }}>
          <button
            type="button"
            onClick={handleSave}
            disabled={saveState === 'saving' || saveState === 'saved'}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.375rem',
              padding: '0.375rem 0.75rem', fontSize: '0.8125rem', fontWeight: 600,
              background: saveState === 'saved' ? '#16a34a' : 'var(--color-primary)',
              color: 'white', border: 'none', borderRadius: 'var(--radius-sm)',
              cursor: saveState === 'saving' || saveState === 'saved' ? 'default' : 'pointer',
              opacity: saveState === 'saving' ? 0.6 : 1,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 1v12M1 7h12" />
            </svg>
            {saveState === 'idle' && 'In Kontakt speichern'}
            {saveState === 'saving' && 'Speichern...'}
            {saveState === 'saved' && 'Gespeichert'}
            {saveState === 'error' && 'Fehler - erneut'}
          </button>
          <a
            href={event.webLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.375rem',
              padding: '0.375rem 0.75rem', fontSize: '0.8125rem', fontWeight: 600,
              background: '#0078d4', color: 'white', border: 'none',
              borderRadius: 'var(--radius-sm)', textDecoration: 'none',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2H2a1 1 0 00-1 1v10a1 1 0 001 1h12a1 1 0 001-1V8" />
              <path d="M10 1h5v5" />
              <path d="M6 10L15 1" />
            </svg>
            In Outlook öffnen
          </a>
        </div>

        {/* Details */}
        <div style={{ padding: '0 1.25rem 1.25rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: '0.375rem 0.75rem', fontSize: '0.8125rem' }}>
            <span style={{ color: 'var(--color-text-muted)' }}>Zeitraum</span>
            <span style={{ color: 'var(--color-text)' }}>
              {formatEventDate(event.start.dateTime)} – {new Date(event.end.dateTime).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
            </span>

            {event.location?.displayName && (
              <>
                <span style={{ color: 'var(--color-text-muted)' }}>Ort</span>
                <span style={{ color: 'var(--color-text)' }}>{event.location.displayName}</span>
              </>
            )}

            <span style={{ color: 'var(--color-text-muted)' }}>Organisator</span>
            <span style={{ color: 'var(--color-text)' }}>{organizerName}</span>

            {attendeeNames && (
              <>
                <span style={{ color: 'var(--color-text-muted)' }}>Teilnehmer</span>
                <span style={{ color: 'var(--color-text)' }}>{attendeeNames}</span>
              </>
            )}

            {event.isOnlineMeeting && event.onlineMeetingUrl && (
              <>
                <span style={{ color: 'var(--color-text-muted)' }}>Meeting-Link</span>
                <a href={event.onlineMeetingUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)' }}>
                  Beitreten
                </a>
              </>
            )}
          </div>

          {/* Termin-Beschreibung */}
          {event.body?.content && (
            event.body.contentType === 'html' ? (
              <div
                dangerouslySetInnerHTML={{ __html: event.body.content }}
                style={{
                  marginTop: '0.75rem', padding: '0.75rem',
                  background: 'var(--color-bg)', borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--color-border)',
                  fontSize: '0.8125rem', color: 'var(--color-text)',
                  maxHeight: 300, overflow: 'auto',
                }}
              />
            ) : (
              <div style={{
                marginTop: '0.75rem', padding: '0.75rem',
                background: 'var(--color-bg)', borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--color-border)',
                fontSize: '0.8125rem', color: 'var(--color-text)',
                whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto',
              }}>
                {event.body.content}
              </div>
            )
          )}

          {/* Kommentar */}
          {saveState !== 'saved' && (
            <div style={{ marginTop: '0.75rem' }}>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Optionaler Kommentar beim Speichern..."
                style={{
                  width: '100%', padding: '0.5rem 0.75rem',
                  border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
                  fontSize: '0.8125rem', resize: 'vertical', minHeight: 48,
                  background: 'var(--color-bg-card)', color: 'var(--color-text)',
                  boxSizing: 'border-box', outline: 'none',
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Hauptkomponente ── */

export function CalendarTab({ contactId }: Props) {
  const { data: integrationsData } = useIntegrations();
  const isConnected = integrationsData?.data?.some(
    (i) => i.provider === 'MICROSOFT_365' && i.connected,
  ) ?? false;

  const { data, isLoading, error } = useContactCalendar(contactId);
  const events: M365CalendarEvent[] = data?.data ?? [];
  const [selectedEvent, setSelectedEvent] = useState<M365CalendarEvent | null>(null);

  if (!isConnected) {
    return (
      <div style={{ padding: '1.5rem 0', textAlign: 'center' }}>
        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9375rem', marginBottom: '1rem' }}>
          Verbinden Sie Microsoft 365, um Kalendertermine zu diesem Kontakt zu sehen.
        </p>
        <button
          type="button"
          onClick={() => integrationsApi.connectM365()}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.4375rem 1rem', background: 'var(--color-primary)',
            color: 'white', border: 'none', borderRadius: 'var(--radius-sm)',
            fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer',
          }}
        >
          Microsoft 365 verbinden
        </button>
      </div>
    );
  }

  if (isLoading) {
    return <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', padding: '1rem 0' }}>Laden...</p>;
  }

  if (error) {
    return <p style={{ color: 'var(--color-error)', fontSize: '0.875rem', padding: '1rem 0' }}>Kalendertermine konnten nicht geladen werden.</p>;
  }

  if (events.length === 0) {
    return <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', padding: '1rem 0' }}>Keine Kalendertermine mit diesem Kontakt gefunden.</p>;
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {events.map((event) => (
          <div
            key={event.id}
            onClick={() => setSelectedEvent(event)}
            style={{
              display: 'block', padding: '0.75rem 1rem',
              background: 'var(--color-bg-card)', border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)', cursor: 'pointer',
              borderLeft: '3px solid var(--color-primary)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text)', flex: 1 }}>
                {event.subject}
              </span>
              {event.isOnlineMeeting && (
                <span style={{
                  fontSize: '0.6875rem', fontWeight: 600,
                  padding: '0.125rem 0.375rem', background: '#dbeafe', color: '#1e40af',
                  borderRadius: '999px', flexShrink: 0,
                }}>
                  Online
                </span>
              )}
            </div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>
              {formatEventDate(event.start.dateTime)} – {new Date(event.end.dateTime).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
            </div>
            {event.location?.displayName && (
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: '0.125rem' }}>
                {event.location.displayName}
              </div>
            )}
          </div>
        ))}
      </div>

      {selectedEvent && (
        <CalendarDetailModal
          event={selectedEvent}
          contactId={contactId}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </>
  );
}
